/**
 * Accessibility utilities for Atlas v2
 * Provides focus management, ARIA helpers, and keyboard navigation utilities
 */

import { RefObject } from 'react';

export interface FocusableElement extends HTMLElement {
  focus(): void;
}

/**
 * Focus Management Utilities
 */
export class FocusManager {
  private static focusableSelectors = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
    '[role="button"]:not([disabled])',
    '[role="link"]',
    '[role="menuitem"]',
    '[role="option"]',
  ].join(', ');

  /**
   * Get all focusable elements within a container
   */
  static getFocusableElements(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll(this.focusableSelectors));
  }

  /**
   * Focus the first focusable element in a container
   */
  static focusFirst(container: HTMLElement): boolean {
    const focusable = this.getFocusableElements(container);
    if (focusable.length > 0 && focusable[0]) {
      focusable[0].focus();
      return true;
    }
    return false;
  }

  /**
   * Focus the last focusable element in a container
   */
  static focusLast(container: HTMLElement): boolean {
    const focusable = this.getFocusableElements(container);
    const lastElement = focusable[focusable.length - 1];
    if (focusable.length > 0 && lastElement) {
      lastElement.focus();
      return true;
    }
    return false;
  }

  /**
   * Trap focus within a container (for modals, dropdowns)
   */
  static trapFocus(container: HTMLElement, event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;

    const focusable = this.getFocusableElements(container);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === first && last) {
        event.preventDefault();
        last.focus();
      }
    } else {
      // Tab
      if (document.activeElement === last && first) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  /**
   * Create a focus trap hook for React components
   */
  static createFocusTrap(containerRef: RefObject<HTMLElement>) {
    return (event: KeyboardEvent) => {
      if (containerRef.current) {
        this.trapFocus(containerRef.current, event);
      }
    };
  }
}

/**
 * ARIA Utilities
 */
export class AriaHelper {
  /**
   * Generate unique IDs for ARIA relationships
   */
  private static idCounter = 0;
  
  static generateId(prefix: string = 'atlas'): string {
    return `${prefix}-${++this.idCounter}-${Date.now()}`;
  }

  /**
   * ARIA live region announcer for screen readers
   */
  private static liveRegion: HTMLElement | null = null;

  static announce(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    if (!this.liveRegion) {
      this.liveRegion = document.createElement('div');
      this.liveRegion.setAttribute('aria-live', priority);
      this.liveRegion.setAttribute('aria-atomic', 'true');
      this.liveRegion.style.position = 'absolute';
      this.liveRegion.style.left = '-10000px';
      this.liveRegion.style.width = '1px';
      this.liveRegion.style.height = '1px';
      this.liveRegion.style.overflow = 'hidden';
      document.body.appendChild(this.liveRegion);
    }

    // Clear previous message and add new one
    this.liveRegion.textContent = '';
    setTimeout(() => {
      if (this.liveRegion) {
        this.liveRegion.textContent = message;
      }
    }, 100);
  }

  /**
   * Create ARIA describedby relationship
   */
  static createDescribedBy(controlId: string, descriptionText: string): {
    controlProps: { 'aria-describedby': string };
    descriptionProps: { id: string; children: string };
  } {
    const descriptionId = `${controlId}-description`;
    return {
      controlProps: { 'aria-describedby': descriptionId },
      descriptionProps: { id: descriptionId, children: descriptionText },
    };
  }

  /**
   * Create ARIA labelledby relationship
   */
  static createLabelledBy(controlId: string, labelText: string): {
    controlProps: { 'aria-labelledby': string };
    labelProps: { id: string; children: string };
  } {
    const labelId = `${controlId}-label`;
    return {
      controlProps: { 'aria-labelledby': labelId },
      labelProps: { id: labelId, children: labelText },
    };
  }
}

/**
 * Keyboard Navigation Utilities
 */
export class KeyboardNav {
  /**
   * Standard keyboard event handlers
   */
  static createKeyHandler(handlers: Record<string, (event: KeyboardEvent) => void>) {
    return (event: KeyboardEvent) => {
      const handler = handlers[event.key];
      if (handler) {
        handler(event);
      }
    };
  }

  /**
   * Arrow key navigation for lists and grids
   */
  static createArrowNavigation(options: {
    container: RefObject<HTMLElement>;
    itemSelector?: string;
    orientation?: 'horizontal' | 'vertical' | 'both';
    wrap?: boolean;
  }) {
    const { container, itemSelector = '[role="option"], button, a', orientation = 'vertical', wrap = true } = options;

    return (event: KeyboardEvent) => {
      if (!container.current) return;

      const items = Array.from(container.current.querySelectorAll(itemSelector)) as HTMLElement[];
      const currentIndex = items.findIndex(item => item === document.activeElement);
      
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;

      switch (event.key) {
        case 'ArrowUp':
          if (orientation === 'vertical' || orientation === 'both') {
            event.preventDefault();
            nextIndex = currentIndex > 0 ? currentIndex - 1 : (wrap ? items.length - 1 : 0);
          }
          break;
        case 'ArrowDown':
          if (orientation === 'vertical' || orientation === 'both') {
            event.preventDefault();
            nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : (wrap ? 0 : items.length - 1);
          }
          break;
        case 'ArrowLeft':
          if (orientation === 'horizontal' || orientation === 'both') {
            event.preventDefault();
            nextIndex = currentIndex > 0 ? currentIndex - 1 : (wrap ? items.length - 1 : 0);
          }
          break;
        case 'ArrowRight':
          if (orientation === 'horizontal' || orientation === 'both') {
            event.preventDefault();
            nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : (wrap ? 0 : items.length - 1);
          }
          break;
        case 'Home':
          event.preventDefault();
          nextIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          nextIndex = items.length - 1;
          break;
      }

      const nextItem = items[nextIndex];
      if (nextIndex !== currentIndex && nextItem) {
        nextItem.focus();
      }
    };
  }
}

/**
 * Responsive Design Utilities
 */
export class ResponsiveHelper {
  /**
   * Breakpoint definitions matching Ant Design
   */
  static breakpoints = {
    xs: 480,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1600,
  } as const;

  /**
   * Get current breakpoint
   */
  static getCurrentBreakpoint(): keyof typeof ResponsiveHelper.breakpoints {
    const width = window.innerWidth;
    
    if (width < this.breakpoints.xs) return 'xs';
    if (width < this.breakpoints.sm) return 'sm';
    if (width < this.breakpoints.md) return 'md';
    if (width < this.breakpoints.lg) return 'lg';
    if (width < this.breakpoints.xl) return 'xl';
    return 'xxl';
  }

  /**
   * Check if current viewport matches breakpoint
   */
  static isBreakpoint(breakpoint: keyof typeof ResponsiveHelper.breakpoints, operator: '>=' | '<=' = '>='): boolean {
    const width = window.innerWidth;
    const breakpointWidth = this.breakpoints[breakpoint];
    
    return operator === '>=' ? width >= breakpointWidth : width <= breakpointWidth;
  }

  /**
   * Touch-friendly tap target size checker
   */
  static validateTouchTarget(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    const minSize = 44; // WCAG recommended minimum touch target size
    
    return rect.width >= minSize && rect.height >= minSize;
  }
}

/**
 * Loading State Utilities
 */
export class LoadingStateHelper {
  /**
   * Create consistent loading announcement
   */
  static announceLoading(resource: string): void {
    AriaHelper.announce(`Loading ${resource}...`, 'polite');
  }

  /**
   * Announce completion
   */
  static announceComplete(resource: string, count?: number): void {
    const message = count !== undefined 
      ? `${resource} loaded successfully. ${count} items found.`
      : `${resource} loaded successfully.`;
    AriaHelper.announce(message, 'polite');
  }

  /**
   * Announce error
   */
  static announceError(resource: string, error: string): void {
    AriaHelper.announce(`Error loading ${resource}: ${error}`, 'assertive');
  }
}

/**
 * Form Accessibility Utilities
 */
export class FormAccessibility {
  /**
   * Create accessible form field with error handling
   */
  static createFieldProps(fieldId: string, label: string, error?: string, help?: string) {
    const props = {
      field: {
        id: fieldId,
        'aria-label': label,
        'aria-invalid': !!error,
        'aria-describedby': undefined as string | undefined,
      },
      label: {
        htmlFor: fieldId,
        children: label,
      },
      error: error ? {
        id: `${fieldId}-error`,
        role: 'alert',
        children: error,
      } : undefined,
      help: help ? {
        id: `${fieldId}-help`,
        children: help,
      } : undefined,
    };

    // Build describedby list
    const describedBy: string[] = [];
    if (props.error) describedBy.push(props.error.id);
    if (props.help) describedBy.push(props.help.id);
    
    if (describedBy.length > 0) {
      props.field['aria-describedby'] = describedBy.join(' ');
    }

    return props;
  }
}

/**
 * Export all utilities as default object for easy importing
 */
export default {
  FocusManager,
  AriaHelper,
  KeyboardNav,
  ResponsiveHelper,
  LoadingStateHelper,
  FormAccessibility,
};